import { PenaltyReconfirmation } from './PenaltyReconfirmation';
import { CancellationPenaltyLabel } from './CancellationPenaltyLabel';
import { KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  AirportShuttle,
  Attractions,
  ConfirmationNumber,
  DirectionsBoat,
  DirectionsBus,
  DirectionsCar,
  SimCard,
  Tour,
  Train,
  Hotel,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';

import { RuteroVertical } from '@bandido/shared/types';

import { TravelBooking } from '../queries';
import { VoucherAction } from './VoucherAction';
import {
  bookingConfirmBlock,
  bookingStateColor,
  bookingStateLabelKey,
  isOnRequest,
} from '../bookingState';
import { bookingRecoveryChipKey, bookingRecoveryNoticeKey } from '../bookingRecovery';
import {
  BOOKING_LIST_COLUMNS,
  BookingListColumnId,
} from '../bookingList';
import { bookingListAction, bookingListMilestone, bookingListServiceName, bookingListHotelTotal } from '../bookingListRow';
import { bookingServiceMoment } from '../bookingListDays';
import { formatMoney } from '../money';
import { MOBILE_TAP_TARGET } from '../bookingMobile';
import { ActiveOperationChip } from './ActiveOperationStatus';
import { HoldCountdown } from './HoldCountdown';
import { NetNote } from './PriceBlock';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { Link } from '@frontend/features/common/components/Link';
import { addLeadingZeros } from '@frontend/features/common/utils/helpers';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { routes } from '@frontend/router/routes';

const STATE_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  draft: 'default',
  quoted: 'info',
  held: 'info',
  confirmed: 'success',
  cancelled: 'error',
  failed: 'error',
};

/**
 * El ícono con el que se reconoce el vertical de un vistazo. Es un `Record`
 * exhaustivo: un vertical nuevo no compila hasta tener cara (§21.22.4). Son
 * los mismos íconos con los que la navegación nombra cada buscador — la fila
 * no inventa un vocabulario visual propio.
 */
export const BOOKING_VERTICAL_ICONS: Record<RuteroVertical, SvgIconComponent> = {
  rail: Train,
  buses: DirectionsBus,
  ferry: DirectionsBoat,
  tours: Attractions,
  circuits: Tour,
  transfers: AirportShuttle,
  cars: DirectionsCar,
  esims: SimCard,
  passes: ConfirmationNumber,
};

function VerticalIcon({ vertical }: { vertical: string }) {
  if (vertical === 'hotels') return <Hotel fontSize="small" color="action" />;
  const Icon = BOOKING_VERTICAL_ICONS[vertical as RuteroVertical];
  if (!Icon) return null;
  return <Icon fontSize="small" color="action" />;
}

export interface BookingListRowProps {
  booking: TravelBooking;
  /** El día del asesor, para leer "hoy" y "mañana" en la fecha del servicio. */
  today: string;
  locale: string;
  onOpen: (booking: TravelBooking) => void;
  onSync: (booking: TravelBooking) => void;
  /** La fila que se está sincronizando ahora, si hay alguna. */
  syncingId: string | null;
  /**
   * La fila es de un pedido anterior (otro ámbito, otra pestaña) que todavía
   * se está reemplazando: se sigue leyendo, pero no se opera sobre ella
   * (§21.23.4).
   */
  isStale: boolean;
}

/** El estado unificado + su matiz + las tarjetas de recuperación, apilados. */
function StateCell({ booking }: { booking: TravelBooking }) {
  const { t } = useTranslation();
  const recoveryChip = bookingRecoveryChipKey(booking);
  const recoveryNotice = bookingRecoveryNoticeKey(booking);
  return (
    <Stack
      spacing={0.5}
      alignItems="flex-start"
      // El rótulo de una operación estancada es una FRASE: sin dejarlo envolver,
      // esa fila sola le roba media tabla a las columnas que se leen.
      sx={{
        maxWidth: '100%',
        '& .MuiChip-root': { maxWidth: '100%', height: 'auto' },
        '& .MuiChip-label': { whiteSpace: 'normal', display: 'block', py: 0.25 },
      }}
    >
      <Chip
        size="small"
        color={booking.source === 'hotelrs' && booking.unknownProviderState ? 'warning' : bookingStateColor(booking.state, booking.providerStatus, STATE_COLOR)}
        label={t(booking.source === 'hotelrs' && booking.unknownProviderState ? 'hotelReservation.uncertainLabel' : bookingStateLabelKey(booking.state, booking.providerStatus))}
      />
      {recoveryChip && (
        <Tooltip title={recoveryNotice ? t(recoveryNotice) : ''}>
          <Chip size="small" color="warning" label={t(recoveryChip)} />
        </Tooltip>
      )}
      {bookingConfirmBlock(booking) && (
        <Tooltip title={t('travelServices.booking.confirmBlockListTooltip')}>
          <Chip
            size="small"
            color="warning"
            label={t('travelServices.booking.confirmBlockChip')}
          />
        </Tooltip>
      )}
      <ActiveOperationChip booking={booking} />
      {/* Un matiz nativo que no es "on request" no se interpreta: se muestra
          tal cual, sin convertirlo en una tarea. */}
      {booking.providerStatus && !isOnRequest(booking.providerStatus) && (
        <Chip size="small" variant="outlined" label={booking.providerStatus} />
      )}
    </Stack>
  );
}

/** Qué es la reserva y cuándo se presta, en dos renglones. */
function ServiceCell({
  booking,
  today,
  locale,
  withIcon = true,
}: {
  booking: TravelBooking;
  today: string;
  locale: string;
  withIcon?: boolean;
}) {
  const { t } = useTranslation();
  const name = bookingListServiceName(booking, t);
  const moment = bookingServiceMoment(booking, today, locale);
  const day =
    moment.kind === 'today'
      ? t('travelServices.bookingList.day.today')
      : moment.kind === 'tomorrow'
        ? t('travelServices.bookingList.day.tomorrow')
        : moment.dateLabel;

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      {withIcon && (
        <Box pt={0.25}>
          <VerticalIcon vertical={booking.vertical} />
        </Box>
      )}
      <Box minWidth={0}>
        <Typography variant="body2" fontWeight={600}>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {[day, moment.time].filter(Boolean).join(' · ') || '—'}
        </Typography>
      </Box>
    </Stack>
  );
}

/** El titular congelado al reservar y cuántos viajan. */
function HolderCell({ booking }: { booking: TravelBooking }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="body2">{booking.holderName ?? '—'}</Typography>
      {typeof booking.paxCount === 'number' && (
        <Typography variant="caption" color="text.secondary">
          {t('travelServices.paxShort', { count: booking.paxCount })}
        </Typography>
      )}
    </Box>
  );
}

function ProviderCell({ booking }: { booking: TravelBooking }) {
  return (
    <Box>
      <Typography variant="body2">{booking.hotelSnapshot?.supplierDisplayName || booking.provider || '—'}</Typography>
      <Tooltip title={booking.providerReference ?? ''}>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 220 }}>
          {booking.providerReference ?? '—'}
        </Typography>
      </Tooltip>
    </Box>
  );
}

/**
 * El próximo hito. El reloj del hold es el MISMO contador que la barra del
 * modal, así que la fila no puede decir una hora que el detalle ya muestra
 * vencida.
 */
function MilestoneCell({ booking }: { booking: TravelBooking }) {
  const { t } = useTranslation();
  if (booking.source === 'hotelrs')
    return booking.state === 'confirmed' ? (
      <VoucherAction booking={booking} variant="row" allowSend={false} />
    ) : (
      <Typography variant="caption">
        {t(`hotelReservation.phase.${booking.hotelSnapshot?.phase ?? 'unknown'}`)}
      </Typography>
    );
  const milestone = bookingListMilestone(booking);
  if (milestone.kind === 'holdCountdown') {
    return <HoldCountdown expiresAt={booking.expiresAt} />;
  }
  // Confirmada: el hito de los tickets y, al lado, el del voucher de agencia.
  // El voucher decide solo si tiene algo que decir (y solo pregunta por las
  // filas confirmadas que viven en el hub).
  if (milestone.kind === 'ticketsReady') {
    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        <Chip size="small" color="success" variant="outlined" label={t(milestone.textKey)} />
        <VoucherAction booking={booking} variant="row" />
      </Box>
    );
  }
  if (milestone.kind === 'ticketsPending' || milestone.kind === 'awaitingProvider') {
    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        <Chip size="small" color="warning" variant="outlined" label={t(milestone.textKey)} />
        <VoucherAction booking={booking} variant="row" />
      </Box>
    );
  }
  if (milestone.kind === 'recovery') {
    return (
      <Typography variant="caption" color="text.secondary">
        {t(milestone.textKey)}
      </Typography>
    );
  }
  return (
    <Typography variant="caption" color="text.secondary">
      —
    </Typography>
  );
}

/**
 * La plata: el total y, para el asesor, el neto. La decisión de mostrarlo es
 * la MISMA de las cards (`NetNote`/`useNetText`), no una condición nueva.
 * Un neto que no llegó no se dibuja como cero.
 */
function MoneyCell({ booking }: { booking: TravelBooking }) {
  const total =
    booking.source === 'hotelrs'
      ? bookingListHotelTotal(booking)
      : typeof booking.finalAmountMinor === 'number' && booking.currency
        ? formatMoney({
            amountMinor: booking.finalAmountMinor,
            currency: booking.currency,
          })
        : '—';
  const isGross = booking.pricingModel === 'commissionable';
  const showNet =
    booking.source !== 'hotelrs' &&
    Boolean(booking.currency) &&
    (isGross || typeof booking.netAmountMinor === 'number');

  return (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {total}
      </Typography>
      {showNet && (
        <NetNote
          pricing={{
            net: {
              amountMinor: booking.netAmountMinor ?? 0,
              currency: booking.currency as string,
            },
            final: {
              amountMinor: booking.finalAmountMinor ?? 0,
              currency: booking.currency as string,
            },
            isGross,
          }}
        />
      )}
    </Box>
  );
}

/** Contexto comercial del booking; los históricos conservan un vacío explícito. */
function CaseCell({ booking }: { booking: TravelBooking }) {
  const { t } = useTranslation();
  const { ability } = useAuth();
  if (!booking.case) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('travelServices.bookingList.case.none')}
      </Typography>
    );
  }
  const number = addLeadingZeros(booking.case.id, defaultLeadingZeros);
  const content = (
    <Box minWidth={0}>
      <Typography variant="body2" fontWeight={600} noWrap>
        {t('travelServices.bookingList.case.number', { number })}
      </Typography>
      <Tooltip title={booking.case.name}>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {booking.case.name}
        </Typography>
      </Tooltip>
      <Typography variant="caption" color="text.secondary" display="block" noWrap>
        {[booking.case.client.firstName, booking.case.client.lastName]
          .filter(Boolean)
          .join(' ')}
      </Typography>
    </Box>
  );
  return ability?.can('manage', 'Cases') ? (
    <Link
      underline="hover"
      to={routes.dashboard.caseView.replace(':id', String(booking.case.id))}
      onClick={(event) => event.stopPropagation()}
    >
      {content}
    </Link>
  ) : content;
}

function CreatedCell({ booking }: { booking: TravelBooking }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="caption" display="block">
        {new Date(booking.createdAt).toLocaleDateString()}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('travelServices.bookingList.by', {
          who: booking.createdByEmail ?? booking.createdBy,
        })}
      </Typography>
    </Box>
  );
}

/**
 * LA acción de la fila. Es un botón real —nunca la fila entera como único
 * blanco—: con teclado se llega tabulando y se dispara con Enter sin abrir el
 * detalle, y su rótulo dice qué va a pasar.
 */
function RowAction({
  booking,
  onOpen,
  onSync,
  syncingId,
  isStale,
  fullWidth,
}: {
  booking: TravelBooking;
  onOpen: (booking: TravelBooking) => void;
  onSync: (booking: TravelBooking) => void;
  syncingId: string | null;
  isStale: boolean;
  fullWidth?: boolean;
}) {
  const { t } = useTranslation();
  const action = bookingListAction(booking);
  const label = t(action.labelKey);
  return (
    <LoadingButton
      size="small"
      variant={action.kind === 'view' ? 'outlined' : 'contained'}
      loading={action.kind === 'sync' && syncingId === booking.id}
      disabled={isStale}
      fullWidth={fullWidth}
      aria-label={t('travelServices.bookingList.actionAria', {
        action: label,
        service: bookingListServiceName(booking, t),
      })}
      onClick={(event) => {
        // La fila también abre el detalle: sin esto, "Sincronizar" abriría el
        // modal además de sincronizar.
        event.stopPropagation();
        if (action.opensDetail) onOpen(booking);
        else onSync(booking);
      }}
    >
      {label}
    </LoadingButton>
  );
}

/**
 * Una fila de la tabla. Las celdas se dibujan recorriendo la MISMA lista de
 * columnas que el encabezado (`BOOKING_LIST_COLUMNS`) y el mapa de acá es
 * exhaustivo: no puede quedar una columna sin celda ni una celda sin título
 * (§21.9).
 */
export function BookingListRow({
  booking,
  today,
  locale,
  onOpen,
  onSync,
  syncingId,
  isStale,
}: BookingListRowProps) {
  const { t } = useTranslation();
  const cells: Record<BookingListColumnId, ReactNode> = {
    state: <StateCell booking={booking} />,
    service: <ServiceCell booking={booking} today={today} locale={locale} />,
    holder: <HolderCell booking={booking} />,
    provider: <ProviderCell booking={booking} />,
    milestone: <MilestoneCell booking={booking} />,
    cancellationPenalty: <CancellationPenaltyLabel booking={booking} />,
    total: <MoneyCell booking={booking} />,
    case: <CaseCell booking={booking} />,
    created: <CreatedCell booking={booking} />,
    reconfirmation: <PenaltyReconfirmation booking={booking} compact />,
    action: (
      <RowAction
        booking={booking}
        onOpen={onOpen}
        onSync={onSync}
        syncingId={syncingId}
        isStale={isStale}
      />
    ),
  };

  const open = () => onOpen(booking);
  const onKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open();
    }
  };

  return (
    <TableRow
      hover
      // Focusable y con nombre accesible, pero SIN `role="button"`: sobre un
      // `<tr>` ese rol le saca a la tabla su estructura (las celdas dejan de
      // ser celdas). La acción explícita es el botón de la fila.
      tabIndex={0}
      aria-label={t('travelServices.bookingList.rowAria', {
        service: bookingListServiceName(booking, t),
        state: t(bookingStateLabelKey(booking.state, booking.providerStatus)),
      })}
      onClick={open}
      onKeyDown={onKeyDown}
      sx={{ cursor: 'pointer' }}
    >
      {BOOKING_LIST_COLUMNS.map((column) => (
        <TableCell
          key={column.id}
          align={column.align}
          sx={{
            verticalAlign: 'top',
            boxSizing: 'border-box',
            width: column.width,
            maxWidth: column.width,
          }}
        >
          {cells[column.id]}
        </TableCell>
      ))}
    </TableRow>
  );
}

/**
 * La misma fila a 375 px: estado y hito arriba, el servicio al medio, y la
 * fila de cierre con la plata a la izquierda y la acción a la derecha — el
 * patrón que ya cierra las cards de resultados y el modal en mobile. Los
 * blancos táctiles no bajan de 44 px.
 */
export function BookingListCard({
  booking,
  today,
  locale,
  onOpen,
  onSync,
  syncingId,
  isStale,
}: BookingListRowProps) {
  const { t } = useTranslation();
  const open = () => onOpen(booking);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={t('travelServices.bookingList.rowAria', {
        service: bookingListServiceName(booking, t),
        state: t(bookingStateLabelKey(booking.state, booking.providerStatus)),
      })}
      onClick={open}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      border={1}
      borderColor={(theme) => alpha(theme.palette.text.primary, 0.12)}
      borderRadius={2}
      bgcolor="background.paper"
      sx={{ cursor: 'pointer' }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
        px={1.5}
        pt={1.5}
      >
        <StateCell booking={booking} />
        <MilestoneCell booking={booking} />
      </Box>
      <Box px={1.5} py={1.5}>
        <ServiceCell booking={booking} today={today} locale={locale} />
        <CancellationPenaltyLabel booking={booking} />
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {[
            booking.holderName,
            typeof booking.paxCount === 'number'
              ? t('travelServices.paxShort', { count: booking.paxCount })
              : undefined,
            booking.provider,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
        <Box mt={0.75}>
          <CaseCell booking={booking} />
        </Box>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        px={1.5}
        py={1}
        borderTop={1}
        borderColor={(theme) => alpha(theme.palette.text.primary, 0.12)}
        sx={{
          '& .MuiButton-root': {
            minHeight: MOBILE_TAP_TARGET,
            minWidth: MOBILE_TAP_TARGET,
          },
        }}
      >
        <MoneyCell booking={booking} />
        <PenaltyReconfirmation booking={booking} compact />
        <RowAction
          booking={booking}
          onOpen={onOpen}
          onSync={onSync}
          syncingId={syncingId}
          isStale={isStale}
        />
      </Box>
    </Box>
  );
}

/** El encabezado de un día en "Próximas a viajar". */
export function BookingDayHeader({
  label,
  columns,
}: {
  label: string;
  columns: number;
}) {
  return (
    <TableRow>
      <TableCell
        colSpan={columns}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
          py: 0.5,
        }}
      >
        <Typography variant="overline" color="text.secondary" letterSpacing={1}>
          {label}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
